import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Flame, Plus, Trash2, Save, ChevronDown, Loader2, PlayCircle } from 'lucide-react';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { showRewardedAd } from '../lib/admob';
import { hasRewardedUnlock, grantRewardedUnlock } from '../lib/rewardedUnlock';

const DEFAULTS = { weight_kg: 70, height_cm: 170, age: 30, gender: 'male', activity: 'light', goal: 'maintain' };

export default function Tracker() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [target, setTarget] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(DEFAULTS);
  const [today, setToday] = useState({ items: [], totals: { kcal: 0, protein: 0, carbs: 0, fat: 0 } });
  const [week, setWeek] = useState([]);
  const [busy, setBusy] = useState(false);

  const [open, setOpen] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [mealName, setMealName] = useState('');
  const [mealKcal, setMealKcal] = useState('');
  const [mealServings, setMealServings] = useState(1);
  const [pickedRecipeId, setPickedRecipeId] = useState('');

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [pRes, dRes, wRes] = await Promise.all([
        api.get('/profile'),
        api.get('/log/today'),
        api.get('/log/week'),
      ]);
      setProfile(pRes.data.profile);
      setTarget(pRes.data.target_kcal);
      if (!pRes.data.profile) setEditing(true);
      else setForm({ ...DEFAULTS, ...pRes.data.profile });
      setToday(dRes.data);
      setWeek(wRes.data.days || []);
    } catch (e) { console.warn('[Tracker] load failed', e); }
  }, [user]);

  useEffect(() => { load(); api.get('/recipes').then(({ data }) => setRecipes(data)); }, [load]);

  const saveProfile = async () => {
    setBusy(true);
    try {
      const payload = {
        weight_kg: Number(form.weight_kg), height_cm: Number(form.height_cm),
        age: Number(form.age), gender: form.gender, activity: form.activity, goal: form.goal,
      };
      const { data } = await api.post('/profile', payload);
      setProfile(payload); setTarget(data.target_kcal); setEditing(false);
      toast.success(t('profileSaved'));
    } catch { toast.error('Error'); }
    finally { setBusy(false); }
  };

  const addMeal = async () => {
    if (!mealName.trim() && !pickedRecipeId) return;
    let payload;
    if (pickedRecipeId) {
      const r = recipes.find(x => x.recipe_id === pickedRecipeId);
      if (!r) return;
      payload = {
        name: lang === 'ar' ? r.title_ar : r.title_en,
        kcal: r?.nutrition?.kcal || 0,
        protein: r?.nutrition?.protein || 0,
        carbs: r?.nutrition?.carbs || 0,
        fat: r?.nutrition?.fat || 0,
        servings: Number(mealServings) || 1,
        recipe_id: r.recipe_id,
      };
    } else {
      payload = {
        name: mealName.trim(),
        kcal: Number(mealKcal) || 0,
        servings: Number(mealServings) || 1,
      };
    }
    try {
      await api.post('/log', payload);
      toast.success(t('mealAdded'));
      setOpen(false);
      setMealName(''); setMealKcal(''); setMealServings(1); setPickedRecipeId('');
      load();
    } catch { toast.error('Error'); }
  };

  const removeEntry = async (id) => {
    await api.delete(`/log/${id}`);
    load();
  };

  const remaining = useMemo(() => (target ? target - today.totals.kcal : 0), [target, today]);
  const pct = useMemo(() => (target ? Math.min(100, Math.round((today.totals.kcal / target) * 100)) : 0), [target, today]);

  if (!user) {
    return (
      <div className="text-center py-20 space-y-4" data-testid="tracker-page">
        <Activity className="h-12 w-12 mx-auto text-[#C05A42]" />
        <p className="text-[#5C4D44]">{t('needLogin')}</p>
        <Link to="/login" className="inline-flex items-center rounded-full bg-[#C05A42] text-white px-6 py-3 text-sm font-medium hover:bg-[#A04832]" data-testid="login-link">{t('loginWithGoogle')}</Link>
      </div>
    );
  }

  if (!user.is_premium && !hasRewardedUnlock()) {
    const startTrial = async () => {
      try { await api.post('/trial/start'); window.location.reload(); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };
    const watchRewardedAd = async () => {
      const earned = await showRewardedAd();
      if (earned) {
        grantRewardedUnlock();
        toast.success('🎉 مبروك! Premium مفتوح لمدة 24 ساعة');
        setTimeout(() => window.location.reload(), 900);
      } else {
        toast.info('لم يكتمل الإعلان — حاول مرة أخرى');
      }
    };
    return (
      <div className="text-center py-16 space-y-6 max-w-xl mx-auto" data-testid="tracker-paywall">
        <div className="inline-flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-[#C05A42] to-[#E29F48] text-white mx-auto shadow-lg">
          <Activity className="h-10 w-10" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">{t('tracker')}</h1>
        <p className="text-[#5C4D44] leading-relaxed">{t('premiumDesc')}</p>
        <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
          {!user.trial_used && (
            <Button onClick={startTrial} data-testid="paywall-trial-btn" variant="outline"
              className="rounded-full border-2 border-[#7A8B76] text-[#4A5D23] hover:bg-[#F1F4EE] px-7 py-5 font-semibold">
              ✨ {t('startTrial')}
            </Button>
          )}
          <Link to="/premium" data-testid="tracker-upgrade-link" className="inline-flex items-center gap-2 rounded-full bg-[#C05A42] text-white px-8 py-3.5 font-semibold hover:bg-[#A04832] transition-all hover:-translate-y-0.5 shadow-md">
            <Activity className="h-4 w-4" /> {t('subscribe')}
          </Link>
        </div>
        {/* Rewarded-ad unlock — a free way to try Premium for 24h without paying.
            Highest-eCPM ad format so this is our best non-subscription monetisation lever. */}
        <div className="pt-3 border-t border-[#F1EBE3]">
          <p className="text-sm text-[#5C4D44] mb-3">لست جاهزاً بعد؟</p>
          <Button
            onClick={watchRewardedAd}
            data-testid="watch-ad-unlock-btn"
            variant="ghost"
            className="rounded-full text-[#5C4D44] hover:bg-[#F1EBE3] px-6 py-4 font-medium"
          >
            <PlayCircle className="h-5 w-5 me-2 text-[#C05A42]" />
            شاهد إعلاناً وافتح Premium لمدة 24 ساعة مجاناً
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tracker-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C05A42] font-semibold flex items-center gap-2"><Activity className="h-3.5 w-3.5" /> {t('tracker')}</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-display font-bold tracking-tight">{t('tracker')}</h1>
        </div>
        {profile && !editing && (
          <Button onClick={() => setEditing(true)} variant="outline" className="rounded-full" data-testid="edit-profile">{t('setupProfile')}</Button>
        )}
      </div>

      {(!profile || editing) && (
        <div className="rounded-3xl bg-white border border-black/5 p-6 space-y-4" data-testid="profile-form">
          <h2 className="font-display text-xl font-semibold">{t('setupProfile')}</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><label className="text-xs text-[#5C4D44]">{t('weight')}</label>
              <Input type="number" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} data-testid="profile-weight" /></div>
            <div><label className="text-xs text-[#5C4D44]">{t('height')}</label>
              <Input type="number" value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} data-testid="profile-height" /></div>
            <div><label className="text-xs text-[#5C4D44]">{t('age')}</label>
              <Input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} data-testid="profile-age" /></div>
            <div><label className="text-xs text-[#5C4D44]">{t('gender')}</label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="profile-gender"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">{t('male')}</SelectItem>
                  <SelectItem value="female">{t('female')}</SelectItem>
                </SelectContent>
              </Select></div>
            <div><label className="text-xs text-[#5C4D44]">{t('activity')}</label>
              <Select value={form.activity} onValueChange={(v) => setForm({ ...form, activity: v })}>
                <SelectTrigger data-testid="profile-activity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sedentary">{t('sedentary')}</SelectItem>
                  <SelectItem value="light">{t('light')}</SelectItem>
                  <SelectItem value="moderate">{t('moderate')}</SelectItem>
                  <SelectItem value="active">{t('active')}</SelectItem>
                </SelectContent>
              </Select></div>
            <div><label className="text-xs text-[#5C4D44]">{t('goal') || t('goalLabel')}</label>
              <Select value={form.goal} onValueChange={(v) => setForm({ ...form, goal: v })}>
                <SelectTrigger data-testid="profile-goal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lose">{t('lose')}</SelectItem>
                  <SelectItem value="maintain">{t('maintain')}</SelectItem>
                  <SelectItem value="gain">{t('gain')}</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          <Button onClick={saveProfile} disabled={busy} data-testid="profile-save" className="rounded-full bg-[#C05A42] hover:bg-[#A04832] text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Save className="h-4 w-4 me-2" />}{t('save')}
          </Button>
        </div>
      )}

      {target && profile && !editing && (
        <>
          {/* Today dashboard */}
          <div className="rounded-3xl bg-gradient-to-br from-[#C05A42] to-[#E29F48] p-6 sm:p-8 text-white shadow-lg" data-testid="today-card">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/80">{t('dailyTarget')}</div>
                <div className="mt-1 text-4xl sm:text-5xl font-display font-bold tabular-nums">{target} <span className="text-lg font-normal">{t('kcal')}</span></div>
              </div>
              <div className="relative h-32 w-32">
                <svg viewBox="0 0 36 36" className="absolute inset-0">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#fff" strokeWidth="3"
                    strokeDasharray={`${pct}, 100`} strokeLinecap="round" transform="rotate(-90 18 18)" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-2xl font-display font-bold tabular-nums">{pct}%</div>
                  <div className="text-xs">{t('consumed')}</div>
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
              <div className="bg-white/15 backdrop-blur-md rounded-2xl p-3">
                <div className="text-white/80 text-xs">{t('consumed')}</div>
                <div className="font-display font-bold text-xl tabular-nums">{today.totals.kcal}</div>
              </div>
              <div className="bg-white/15 backdrop-blur-md rounded-2xl p-3">
                <div className="text-white/80 text-xs">{t('remaining')}</div>
                <div className="font-display font-bold text-xl tabular-nums">{Math.max(0, remaining)}</div>
              </div>
              <div className="bg-white/15 backdrop-blur-md rounded-2xl p-3">
                <div className="text-white/80 text-xs">P / C / F (g)</div>
                <div className="font-display font-bold text-sm tabular-nums">{today.totals.protein} / {today.totals.carbs} / {today.totals.fat}</div>
              </div>
            </div>
          </div>

          {/* Today log */}
          <div>
            <div className="flex items-end justify-between mb-4">
              <h2 className="text-xl font-display font-semibold">{t('todayLog')}</h2>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="open-add-meal" className="rounded-full bg-[#C05A42] hover:bg-[#A04832] text-white">
                    <Plus className="h-4 w-4 me-1" /> {t('addMeal')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#F9F6F0]">
                  <DialogHeader><DialogTitle className="font-display">{t('addMeal')}</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-2">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[#5C4D44]">{t('fromRecipe')}</label>
                      <Select value={pickedRecipeId} onValueChange={setPickedRecipeId}>
                        <SelectTrigger data-testid="pick-recipe"><SelectValue placeholder={t('pickRecipe')} /></SelectTrigger>
                        <SelectContent className="max-h-80">
                          {recipes.map((r) => (
                            <SelectItem key={r.recipe_id} value={r.recipe_id}>
                              {(lang === 'ar' ? r.title_ar : r.title_en)} — {r?.nutrition?.kcal} {t('kcal')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-center text-xs text-[#5C4D44]">— {t('or') || 'OR'} —</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2"><label className="text-xs text-[#5C4D44]">{t('mealName')}</label>
                        <Input value={mealName} onChange={(e) => setMealName(e.target.value)} placeholder={t('customMeal')} data-testid="meal-name" /></div>
                      <div><label className="text-xs text-[#5C4D44]">{t('kcal')}</label>
                        <Input type="number" value={mealKcal} onChange={(e) => setMealKcal(e.target.value)} data-testid="meal-kcal" /></div>
                      <div><label className="text-xs text-[#5C4D44]">{t('servings')}</label>
                        <Input type="number" value={mealServings} onChange={(e) => setMealServings(e.target.value)} data-testid="meal-servings" /></div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={addMeal} data-testid="confirm-add-meal" className="rounded-full bg-[#C05A42] hover:bg-[#A04832] text-white w-full">
                      <Plus className="h-4 w-4 me-1" /> {t('addMeal')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {today.items.length === 0 ? (
              <p className="text-[#5C4D44] text-center py-8 bg-white border border-black/5 rounded-2xl">—</p>
            ) : (
              <ul className="space-y-2" data-testid="today-log-list">
                {today.items.map((it) => (
                  <li key={it.entry_id} className="flex items-center justify-between rounded-2xl bg-white border border-black/5 p-4">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#2C2420] truncate">{it.name}</div>
                      {it.servings !== 1 && <div className="text-xs text-[#5C4D44]">×{it.servings}</div>}
                    </div>
                    <div className="inline-flex items-center gap-1 text-[#C05A42] font-display font-semibold mx-3"><Flame className="h-4 w-4" /> {it.kcal}</div>
                    <button onClick={() => removeEntry(it.entry_id)} data-testid={`del-${it.entry_id}`} className="text-[#5C4D44] hover:text-[#C05A42]">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Week history */}
          {week.length > 0 && (
            <div>
              <h2 className="text-xl font-display font-semibold mb-4">{t('weekHistory')}</h2>
              <div className="rounded-3xl bg-white border border-black/5 p-5">
                <div className="flex items-end gap-2 h-40">
                  {week.map((d) => {
                    const pctH = target ? Math.min(100, Math.round((d.kcal / target) * 100)) : 0;
                    const over = target && d.kcal > target;
                    const label = d.date.slice(5);
                    return (
                      <div key={d.date} className="flex-1 flex flex-col items-center gap-2 justify-end">
                        <div className="text-[10px] tabular-nums text-[#5C4D44]">{d.kcal}</div>
                        <div className={`w-full rounded-t-xl ${over ? 'bg-[#C05A42]' : 'bg-[#7A8B76]'} transition-all`} style={{ height: `${Math.max(2, pctH)}%` }} />
                        <div className="text-[10px] text-[#5C4D44]">{label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
